import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

// Placeholder images for high-end visual appeal
const perfectFitImages = {
  hero: "https://images.unsplash.com/photo-1505691938895-1758d7eaa511?auto=format&fit=crop&w=1200&q=80",
  detail: "https://images.unsplash.com/photo-1600585152220-90363fe7e115?auto=format&fit=crop&w=600&q=80",
  conservatory: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=600&q=80",
};

function PerfectFitBlinds() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-12 text-gray-900">
      <Helmet>
        <title>Perfect Fit Blinds UK | No-Drill Window Blinds for uPVC</title>
        <meta
          name="description"
          content="Shop No-Drill Perfect Fit Blinds for uPVC windows and doors. Easy to install, sleek, and safe. Available in Roller, Pleated, and Venetian styles at MF Blinds."
        />
        <meta name="keywords" content="Perfect Fit Blinds, No Drill Blinds UK, uPVC Window Blinds, Conservatory Blinds, Clip-in Blinds, Child Safe Blinds" />
      </Helmet>

      {/* --- 1. Hero / Introduction --- */}
      <header className="grid lg:grid-cols-2 gap-12 items-center mb-20">
        <div>
          <span className="text-blue-600 font-bold tracking-widest uppercase text-sm">The No-Drill Revolution</span>
          <h1 className="text-4xl md:text-6xl font-extrabold mt-2 mb-6 leading-tight">
            Perfect Fit <span className="text-blue-600">Blinds</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Revolutionize your home with the UK's favorite <strong>no-drill window solution</strong>. Designed specifically for uPVC windows and doors, Perfect Fit blinds snap directly into your window frame—no screws, no mess, and no damage.
          </p>
          <div className="flex flex-wrap gap-4">
            <button className="bg-blue-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-blue-700 transition shadow-lg">
              Shop The Collection
            </button>
            <Link to="/how-to-measure-blinds" className="border-2 border-gray-300 px-8 py-4 rounded-lg font-bold hover:bg-gray-50 transition">
              How to Measure
            </Link>
          </div>
        </div>
        <div className="relative">
          <img 
            src={perfectFitImages.hero} 
            alt="Seamless Perfect Fit blinds on a modern patio door" 
            className="rounded-2xl shadow-2xl"
          />
          <div className="absolute -bottom-6 -left-6 bg-white p-6 shadow-xl rounded-xl hidden md:block">
            <p className="text-3xl font-bold text-blue-600">100%</p>
            <p className="text-sm font-semibold text-gray-500 uppercase">Screw-Free Install</p>
          </div>
        </div>
      </header>

      {/* --- 2. Key Selling Points (Viral Keywords) --- */}
      <section className="bg-gray-50 rounded-3xl p-10 mb-20">
        <h2 className="text-3xl font-bold text-center mb-12">Why Everyone is Switching to Perfect Fit</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="text-4xl mb-4">🏠</div>
            <h3 className="text-xl font-bold mb-2">Zero Damage</h3>
            <p className="text-gray-600 italic">"The renter's dream."</p>
            <p className="mt-2">Ideal for rental properties as they leave no holes in window frames or walls.</p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-4">🛡️</div>
            <h3 className="text-xl font-bold mb-2">Child & Pet Safe</h3>
            <p className="text-gray-600 italic">"Cord-free peace of mind."</p>
            <p className="mt-2">Naturally cordless design makes them the safest choice for nurseries and playrooms.</p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-4">☀️</div>
            <h3 className="text-xl font-bold mb-2">Thermal Insulation</h3>
            <p className="text-gray-600 italic">"Save on energy bills."</p>
            <p className="mt-2">The snug fit minimizes gaps, keeping heat in during winter and out during summer.</p>
          </div>
        </div>
      </section>

      {/* --- 3. Style Options --- */}
      <section className="mb-20">
        <h2 className="text-3xl font-bold mb-10">Available Styles in Perfect Fit</h2>
        <div className="grid md:grid-cols-2 gap-12">
          <div className="flex gap-6 items-start">
            <img src={perfectFitImages.detail} alt="Pleated perfect fit" className="w-32 h-32 rounded-lg object-cover" />
            <div>
              <h4 className="text-xl font-bold mb-2">Pleated & Cellular</h4>
              <p className="text-gray-600 mb-2">The most popular choice for conservatories. Cellular fabrics offer incredible $U-value$ efficiency.</p>
              <Link to="/pleated-blinds" className="text-blue-600 font-semibold hover:underline text-sm">Explore Pleated →</Link>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center text-4xl">🪟</div>
            <div>
              <h4 className="text-xl font-bold mb-2">Venetian (Aluminium)</h4>
              <p className="text-gray-600 mb-2">Ultimate light control. Tilt the slats to let light in while maintaining total privacy.</p>
              <Link to="/venetian-blinds" className="text-blue-600 font-semibold hover:underline text-sm">Explore Venetians →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* --- 4. Deep-Dive Content for SEO --- */}
      <article className="prose lg:prose-xl max-w-none border-t pt-16 border-gray-100">
        <h2 className="text-3xl font-bold mb-6">Are Perfect Fit Blinds Right for Your uPVC Windows?</h2>
        <p className="mb-6 text-lg leading-relaxed">
          Perfect Fit blinds are designed to integrate with the <strong>bead</strong> of your window. If you have a rubber gasket between the glass and the frame, these are likely the perfect choice for you. They are particularly effective for <strong>Tilt and Turn windows</strong> and <strong>French Doors</strong>, as the blind moves with the window or door itself, preventing clattering and tangling.
        </p>
        <p className="mb-6 text-lg leading-relaxed">
          For homeowners looking for a sleek, integrated look, these blinds eliminate the need for bulky headrails. They are available in a huge range of frame colors including White, Anthracite Grey, Brown, and Golden Oak to match your existing uPVC perfectly.
        </p>
      </article>

      {/* --- 5. Navigation / Related Products --- */}
      <section className="mt-20 pt-10 border-t border-gray-200">
        <h3 className="text-2xl font-bold mb-6 text-center">Compare With Other Ranges</h3>
        <div className="flex flex-wrap justify-center gap-6">
          <Link 
            to="/roller-blinds" 
            className="px-6 py-3 bg-white border border-gray-300 rounded-full hover:shadow-md transition text-gray-700"
          >
            Classic Roller Blinds
          </Link>
          <Link 
            to="/wooden-blinds" 
            className="px-6 py-3 bg-white border border-gray-300 rounded-full hover:shadow-md transition text-gray-700"
          >
            Real Wooden Blinds
          </Link>
          <Link 
            to="/shutters" 
            className="px-6 py-3 bg-white border border-gray-300 rounded-full hover:shadow-md transition text-gray-700"
          >
            Plantation Shutters
          </Link>
        </div>
      </section>

      {/* --- 6. Footer CTA --- */}
      <footer className="mt-20 bg-blue-50 p-12 rounded-3xl text-center">
        <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Windows?</h2>
        <p className="text-lg text-gray-600 mb-8">Get the look you've always wanted with zero stress and zero drills.</p>
        <button className="bg-blue-600 text-white px-10 py-4 rounded-full text-lg font-bold hover:scale-105 transition-transform shadow-xl">
          Order Free Samples Now
        </button>
      </footer>
    </div>
  );
}

export default PerfectFitBlinds;